import { useState, useEffect, useRef } from 'react';
import { getEmpresaIdFromToken } from '../services/auth';
import {
  subirDocumento,
  listarDocumentos,
  eliminarDocumento,
} from '../services/documento';
import '../styles/Documento.css';

export default function Documento() {
  const [documentos, setDocumentos] = useState([]);
  const [archivoSeleccionado, setArchivoSeleccionado] = useState(null);
  const [descripcion, setDescripcion] = useState('');
  const [subiendo, setSubiendo] = useState(false);
  const [cargandoLista, setCargandoLista] = useState(true);
  const [error, setError] = useState(null);
  const [mensajeExito, setMensajeExito] = useState(null);

  const empresaId = getEmpresaIdFromToken();
  const fileInputRef = useRef(null);

  // Cargar lista de documentos al montar
  useEffect(() => {
    if (empresaId) {
      cargarDocumentos();
    } else {
      setError('No se pudo determinar la empresa del usuario.');
      setCargandoLista(false);
    }
  }, []);

  const cargarDocumentos = async () => {
    try {
      setCargandoLista(true);
      const data = await listarDocumentos(empresaId, true);
      setDocumentos(data || []);
    } catch (err) {
      console.error('❌ Error al listar documentos:', err);
      setError(err.message || 'Error al cargar los documentos');
    } finally {
      setCargandoLista(false);
    }
  };

  const handleSeleccionarArchivo = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const tiposPermitidos = ['application/pdf', 'text/plain'];
    if (!tiposPermitidos.includes(file.type)) {
      setError('Solo se permiten archivos PDF o TXT');
      setArchivoSeleccionado(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setError(null);
    setArchivoSeleccionado(file);
  };

  const handleSubir = async (e) => {
    e.preventDefault();
    setError(null);
    setMensajeExito(null);

    if (!archivoSeleccionado) {
      setError('Selecciona un archivo PDF o TXT antes de subir.');
      return;
    }

    try {
      setSubiendo(true);
      const nuevo = await subirDocumento(empresaId, archivoSeleccionado, descripcion);
      setMensajeExito(`Documento "${nuevo.nombre}" subido y procesado correctamente.`);
      setArchivoSeleccionado(null);
      setDescripcion('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      await cargarDocumentos();
    } catch (err) {
      console.error('❌ Error al subir documento:', err);
      setError(err.message || 'Error al subir el documento');
    } finally {
      setSubiendo(false);
    }
  };

  const handleEliminar = async (id, nombre) => {
    if (!window.confirm(`¿Eliminar el documento "${nombre}"?`)) return;
    try {
      await eliminarDocumento(id);
      setDocumentos((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      console.error('❌ Error al eliminar documento:', err);
      setError(err.message || 'Error al eliminar el documento');
    }
  };

  const formatearTamano = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className="documento-container">
      <div className="documento-header">
        <h2>📄 Documentos del Bot</h2>
        <p>Sube archivos PDF o TXT para que el bot los use como memoria al responder.</p>
      </div>

      {/* FORMULARIO DE SUBIDA */}
      <form className="documento-form" onSubmit={handleSubir}>
        <div className="documento-form-row">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.txt,application/pdf,text/plain"
            onChange={handleSeleccionarArchivo}
            className="documento-input-file"
            disabled={subiendo}
          />
        </div>

        <div className="documento-form-row">
          <input
            type="text"
            placeholder="Descripción (opcional)"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            className="documento-input-texto"
            disabled={subiendo}
            maxLength={200}
          />
        </div>

        {archivoSeleccionado && (
          <div className="documento-archivo-info">
            📎 {archivoSeleccionado.name} ({formatearTamano(archivoSeleccionado.size)})
          </div>
        )}

        <button
          type="submit"
          className="documento-btn-subir"
          disabled={subiendo || !archivoSeleccionado}
        >
          {subiendo ? 'Subiendo...' : '⬆️ Subir documento'}
        </button>
      </form>

      {error && <div className="documento-error">❌ {error}</div>}
      {mensajeExito && <div className="documento-exito">✅ {mensajeExito}</div>}

      {/* LISTA DE DOCUMENTOS */}
      <div className="documento-lista">
        <h3>Documentos activos</h3>

        {cargandoLista ? (
          <p className="documento-lista-mensaje">Cargando documentos...</p>
        ) : documentos.length === 0 ? (
          <p className="documento-lista-mensaje">Aún no has subido documentos.</p>
        ) : (
          <ul className="documento-lista-items">
            {documentos.map((doc) => (
              <li key={doc.id} className="documento-item">
                <div className="documento-item-info">
                  <span className="documento-item-nombre">
                    {doc.tipo_archivo === 'application/pdf' ? '📕' : '📄'} {doc.nombre}
                  </span>
                  <span className="documento-item-meta">
                    {formatearTamano(doc.tamano_bytes)}
                    {doc.descripcion ? ` · ${doc.descripcion}` : ''}
                  </span>
                </div>
                <button
                  type="button"
                  className="documento-btn-eliminar"
                  onClick={() => handleEliminar(doc.id, doc.nombre)}
                  title="Eliminar documento"
                >
                  🗑️
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}